import React, { useCallback, useState, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Grid } from '@material-ui/core';
import Form from '../../form/components/Form';
import { IdPropType, RefPropType } from '../../../proptypes/basic';
import SubmitButton from '../../form/components/SubmitButton';
import DeleteButton from '../../buttons/components/DeleteButton';
import { destroyNurseDeployment } from '../nurseDeploymentsSlice';
import TitleBar from '../../layout/components/TitleBar';
import Typography from '../../mui/Components/Typography';
import ToggleButtons from '../../form/components/ToggleButtons';
import TextInput from '../../form/components/TextInput';
import ConditionalField from '../../form/components/specialized/ConditionalField';
import DateInput from '../../form/components/DateInput';
import Checkbox from '../../form/components/Checkbox';
import CheckInOutDisable from './CheckInOutDisable';
import { useCustomer } from '../../customers/customersSlice';
import {
    showNurseDeployment,
    storeNurseDeployment,
    updateNurseDeployment,
    useNurseDeployment,
} from '../nurseDeploymentsSlice';
import NursePriceFieldView from './NursePriceFieldView';
import A1DocumentList from '../../a1document/components/A1DocumentList';
import ContractSelect from '../../contracts/components/ContractSelect';
import CareNeedSelect from '../../careNeeds/components/CareNeedSelect';
import { destroyA1Document, storeA1Document } from '../../a1document/a1DocumentsSlice';

import { NURSE_DEPLOYMENT_RESOURCE } from '../../api/resources';
import { useCareNeedsByCustomerId } from '../../careNeeds/careNeedsSlice';
import { useInitialValues } from '../../form/hooks';
import { nurseDeploymentSchema } from '../schema';
import NurseAutocomplete from '../../form/components/specialized/autocomplete/NurseAutocomplete';
import AgencyByContractSelect from './AgencyByContractSelect';
import PauseReasonSelect from './PauseReasonSelect';
import { useUser } from '../../auth/hooks';
import { selectAllAgencies } from '../../agencies/agenciesSlice';
import { useMakeSelector } from '../../store/hooks';
import {
    makeContractsByCustomerIdSelector,
} from '../../contracts/contractsSlice';

import {
    useNurseDeploymentsByCustomerContractId,
} from '../nurseDeploymentsSlice';

import { selectTransferByDeploymentId } from '../../nurseTransfers/selectors';
import { TRANSFER_TYPE_ARRIVAL, TRANSFER_TYPE_DEPARTURE } from '../../nurseTransfers/types';
import { selectUserIsRoot, selectUserIsBuHa } from '../../../Modules/auth/selectors';
import { isCommissionDataFixated, isDateInThisFixationMonthOrEarlier } from '../../../helpers/tools';
import { selectAllNurseDeploymentFixations, indexNurseDeploymentFixations } from '../../nurseDeploymentFixation/nurseDeploymentFixationSlice';
import { selectEmployeeById } from '../../employees/employeesSlice';
import { selectLocationById } from '../../locations/locationsSlice';
import { DEFAULT_SYSTEM_FEE, NO_SYSTEM_FEE_LOCATION_IDS } from '../constants';

const DEFAULT_DEPLOYMENT_TYPES = [
    { value: 'nurse', label: 'BK', disabled: false },
    { value: 'pause', label: 'Pause', disabled: false },
    { value: 'pause_fee_based', label: 'Pause (Kostenpflichtig)', disabled: true },
    { value: 'cancellation_period', label: 'Kündigungsfrist', disabled: false },
];

const submitFull = async (
    values,
    deleteA1Docs,
    nurseDeploymentId,
    customerId,
    employeeId,
    copy,
    dispatch
) => {
    const prepped = { ...values, id: copy ? null : nurseDeploymentId, customerId };

    if (!copy) {
        await Promise.all(
            deleteA1Docs.map((id) => {
                return dispatch(destroyA1Document({ id }));
            })
        );
    }

    const result = await dispatch(
        nurseDeploymentId && !copy ? updateNurseDeployment(prepped) : storeNurseDeployment(prepped)
    );

    const { a1From, a1Till } = values;
    if (a1From && a1Till) {
        await dispatch(
            storeA1Document({
                nurseDeploymentId: result.data.id,
                dateFrom: a1From,
                dateTill: a1Till,
                acceptedManuallyAt: new Date(),
                acceptedManuallyBy: employeeId,
            })
        );

        /* a1State has probably changed -> reload nurse deployment...
         * TODO: should this be handled by sockets automatically?
         */
        return dispatch(showNurseDeployment({ id: result.data.id }));
    }

    return result;
};

const NurseDeploymentForm = ({
    careNeedId,
    nurseDeploymentId,
    customerId: customerIdOverride,
    copy,
    onDone,
    submitRef,
}) => {
    const dispatch = useDispatch();
    const [labelCareFrom, setLabelCareFrom] = useState('Betreuung von');
    const [labelCareTill, setLabelCareTill] = useState('Betreuung bis');
    const [lastDeploymentType, setLastDeploymentType] = useState(undefined);
    const [activeCustomerContractId, setActiveCustomerContractId] = useState(undefined);

    const [optionsDeploymentTypes, setOptionsDeploymentTypes] = useState(DEFAULT_DEPLOYMENT_TYPES);
    const [deleteA1DocList, setDeleteA1DocList] = useState([]);
    const [nurseDeployment] = useNurseDeployment(nurseDeploymentId);
    const deploymentArrivalAt = useSelector((state) =>
        selectTransferByDeploymentId(state, nurseDeploymentId, TRANSFER_TYPE_ARRIVAL)
    );
    const deploymentDepartureAt = useSelector((state) =>
        selectTransferByDeploymentId(state, nurseDeploymentId, TRANSFER_TYPE_DEPARTURE)
    );

    const customerId = customerIdOverride || (nurseDeployment && nurseDeployment.customerId);
    const customer = useCustomer(customerId);
    const careNeeds = useCareNeedsByCustomerId(customerId);
    const user = useUser();
    const agencies = useSelector(selectAllAgencies);
    const contracts = useMakeSelector(makeContractsByCustomerIdSelector, customerId);
    const isRoot = useSelector(selectUserIsRoot);
    const isBuHa = useSelector(selectUserIsBuHa);

    const activeEmployeeId = user?.employeeId;
    const employee = useSelector((state) => selectEmployeeById(state, activeEmployeeId));
    const employee_location = useSelector((state) => selectLocationById(state, employee?.locationId));
    const isFranchiseExpert = employee_location?.franchiseType == "Expert";

    // console.log("user => ", user);
    // console.log("employee => ", employee);
    // console.log("employee_location => ", employee_location);
    // console.log('isFranchiseExpert => ', isFranchiseExpert);


    // update the fixation dates so we can work with them
    const dispatch_nurseDeploymentFixations = useDispatch();
    useEffect(() => {
        dispatch_nurseDeploymentFixations(indexNurseDeploymentFixations());
    }, [dispatch_nurseDeploymentFixations]);

    // get all nurse deployment fixations
    const nurseDeploymentFixations = useSelector(selectAllNurseDeploymentFixations);
    // now find the latest fixation date
    const latestFixation = nurseDeploymentFixations.reduce((latest, current) => {
        const currentDate = new Date(current.fixationMonth);
        const latestDate = latest ? new Date(latest.fixationMonth) : null;
        return currentDate > latestDate ? current : latest;
    }, null);
    //console.log("latestFixation => ", latestFixation);

    // check with the billingFrom date if the data is fixated
    const today = new Date();
    //console.log("check with billingTill =>",nurseDeployment?.billingTill, "today => ", today," latestFixation =>" , latestFixation?.fixedAt);
    let is_fixated_from = isCommissionDataFixated(nurseDeployment?.billingFrom, today, latestFixation);
    let is_fixated_till = isCommissionDataFixated(nurseDeployment?.billingTill, today, latestFixation);

    let is_fixated = is_fixated_from || is_fixated_till;
    let is_fixation_overridden = false;
    //console.log("is_fixated_from => ",is_fixated_from," is_fixated_till => ",is_fixated_till," is_fixated => ",is_fixated);

    //let is_fixated_billing_till = false;
    let is_fixated_care_till = false;

    if (is_fixated) {
        // we still can if we're Root or BuHa
        if (isRoot || isBuHa) {
            console.log("disabled is_fixated for Root or BuHa");
            is_fixated = false;
            is_fixation_overridden = true; // we can override the fixation
        } else {
            // check if the fixation date is in this month or earlier
            is_fixated_care_till = isDateInThisFixationMonthOrEarlier(nurseDeployment?.careTill, latestFixation?.fixationMonth);
        }
    }
    //console.log("is_fixated_care_till => ", is_fixated_care_till);
    //console.log("is_fixated => ",is_fixated," fixedAt => ", latestFixation?.fixedAt);

    // only disable the submit button if all conditions are met
    const is_fixated_submit = is_fixated && is_fixated_care_till;

    // for the selected customer contract (agency)
    const [allNurseDeploymentsByCustomerContractId] = useNurseDeploymentsByCustomerContractId(activeCustomerContractId);
    //console.log("allNurseDeploymentsByCustomerContractId => ",activeCustomerContractId, allNurseDeploymentsByCustomerContractId);

    const nurseDepSchema = nurseDeploymentSchema(
        allNurseDeploymentsByCustomerContractId,
        nurseDeployment,
        latestFixation,
        isRoot,
        isBuHa
    );


    //console.log("NurseDeploymentForm=>",nurseDeploymentId,nurseDeployment );
    const initialValues = useInitialValues(nurseDeployment, {
        careNeedId: (careNeeds.length === 1 && careNeeds[0].id) || careNeedId || '',
        customerContractId: '',
        customerContractEndAt: '',
        agencyId: '',
        type: 'nurse',
        nurseId: '',
        careFrom: null,
        careTill: null,
        notes: '',
        arrivalAt: null,
        departureAt: null,
        withoutArrTransfer: false,
        withoutDepTransfer: false,
        a1From: null,
        a1Till: null,
        billingFrom: (deploymentArrivalAt !== undefined && deploymentArrivalAt !== null && deploymentArrivalAt?.date !== null) ? deploymentArrivalAt.date : null,
        billingFromHalf: false,
        billingTill: (deploymentDepartureAt !== undefined && deploymentDepartureAt !== null && deploymentDepartureAt?.date !== null) ? deploymentDepartureAt.date : null,
        billingTillHalf: false,
        priceAgency: '',
        priceAgencyDaily: false,
        surchargeAgency: '',
        a1Notes: '',
        provisionPZH: '',
        surchargePZH: '',
        provisionLocation: '',
        provisionMultiplier: '', // FIXME: missing in form
        pauseReason: '',
        systemFee: '',
    });


    const handleDelete = useCallback(
        () => {
            return dispatch(destroyNurseDeployment({ id: nurseDeploymentId })).then(onDone);
        },
    [dispatch, nurseDeploymentId, onDone]
    );

    const handleSubmit = useCallback(
        (values) => {
            submitFull(
                values,
                deleteA1DocList,
                nurseDeploymentId,
                customerId,
                user.employeeId,
                copy,
                dispatch
            ).then(onDone);
        },
        [
            dispatch,
            nurseDeploymentId,
            customerId,
            copy,
            deleteA1DocList,
            onDone,
            user.employeeId,
        ]
    );

    const handleDisableBreakFeeBased = () => {
        //console.log('handleDisableBreakFeeBased');
        setOptionsDeploymentTypes((prevOptions) =>
            prevOptions.map((option) =>
                option.value === 'pause_fee_based' ? { ...option, disabled: true } : option
            )
        );
    };

    const handleEnableBreakFeeBased = () => {
        //console.log('handleEnableBreakFeeBased');
        setOptionsDeploymentTypes((prevOptions) =>
            prevOptions.map((option) =>
                option.value === 'pause_fee_based' ? { ...option, disabled: false } : option
            )
        );
    };

    const updateBillingDates = useCallback(
        (values, setfieldvalue) => {
            //console.log('updateBillingDates=>', values.type,deploymentArrivalAt?.date, deploymentDepartureAt?.date);
            if (values.type === 'nurse') {
                // get's updated at the TransferForm
            } else if (values.type === 'pause' || values.type === 'pause_fee_based') {
                //console.log('updateBillingDates for pause =>', values.careFrom, values.careTill, 'billingFrom=', values.billingFrom, 'billingTill=', values.billingTill);

                if(values.billingFrom === null || values.billingFrom === undefined) {
                    setfieldvalue('billingFrom', values.careFrom);
                    setfieldvalue('billingTill', values.careTill);
                }
            } else if (values.type === 'cancellation_period') {
                setfieldvalue('billingFrom', values.careFrom);
                setfieldvalue('billingTill', values.careTill);
            }
        },
        []
    );

    const updateContractInformation = useCallback((customerContract, setfieldvalue) => {
        if (customerContract === undefined || customerContract === null) 
            return;
        setActiveCustomerContractId(customerContract.id);
        setfieldvalue('customerContractEndAt', customerContract.endAt);
    }, []);

    /**
     * clear the current calculated and rendered fees
     * @param {object} setfieldvalue 
     */
    const clearBreakFee = useCallback(
        (setfieldvalue) => {
            setfieldvalue('fixedAgencyPrice', 0.0);
            setfieldvalue('priceAgency', 0.0);
            setfieldvalue('provisionPZH', 0.0);
            setfieldvalue('provisionLocation', 0.0);
            setfieldvalue('surchargeAgency', 0.0);
            setfieldvalue('surchargePZH', 0.0);
            setfieldvalue('systemFee', 0.0);
    }, []);

    const updateBreakFee = useCallback(
        (agency, setfieldvalue) => {
            if (agency === undefined) return;

            const locationId = customer[0]?.locationId;
            const hamburgId = parseInt(process.env.REACT_APP_LOCATION_HAMBURG_ID);

            const defaultCost = agency.defaultCostFeeBasedBrake;
            if (defaultCost === undefined || defaultCost === null || defaultCost <= 0.0) return;

            const fixPriceAgency = defaultCost / 2;
            const fixProvisionPZH = fixPriceAgency; // locationId !== hamburgId ? fixPriceAgency / 2 : fixPriceAgency
            const fixProvisionLocation = locationId !== hamburgId ? fixPriceAgency / 2 : '';
            setfieldvalue('fixedAgencyPrice', defaultCost);
            setfieldvalue('priceAgency', fixPriceAgency);
            setfieldvalue('provisionPZH', fixProvisionPZH);
            setfieldvalue('provisionLocation', fixProvisionLocation);
        },
        [customer]
    );

    /**
     * update the system fee if its not already set
     * @param {object} setfieldvalue
     */
    const updateSystemFee = useCallback(
        (setfieldvalue) => {
            const systemFee = DEFAULT_SYSTEM_FEE;
            // only set a default system fee if the customer's location is not excluded (see
            // NO_SYSTEM_FEE_LOCATION_IDS) and if the fee is not already set
            if (
                systemFee === undefined ||
                systemFee === null ||
                systemFee <= 0.0 ||
                NO_SYSTEM_FEE_LOCATION_IDS.includes(customer[0]?.locationId)
            )
                return;
            setfieldvalue('systemFee', systemFee);
        },
        [customer]
    );

    /**
     * make some ui-updates based on the changed deployment type
     * @param {string} newDeploymentType
     * @param {object} setfieldvalue
     * @param {object} values
     */
    const handleOnChangeDeploymentType = (newDeploymentType, setfieldvalue, values) => {
        if (lastDeploymentType !== newDeploymentType) {
            //console.log(`deployment type changed ${lastDeploymentType} => ${newDeploymentType}`);
            switch (newDeploymentType) {
                case 'nurse':
                    setLabelCareFrom('Betreuung geplant von');
                    setLabelCareTill('Betreuung geplant bis');
                    break;
                case 'pause_fee_based':
                case 'pause':
                    setLabelCareFrom('Pause von');
                    setLabelCareTill('Pause bis');
                    break;
                case 'cancellation_period':
                    setLabelCareFrom('Kündigungsfrist von');
                    setLabelCareTill('Kündigungsfrist bis');
                    break;
                default:
                    setLabelCareFrom('Betreuung von');
                    setLabelCareTill('Betreuung bis');
                    break;
            }

            if (newDeploymentType !== 'pause_fee_based') {
                setfieldvalue('fixedAgencyPrice', undefined);
            }

            if (newDeploymentType === 'pause' || lastDeploymentType === 'pause_fee_based') {
                clearBreakFee(setfieldvalue);
            }

            if (newDeploymentType === 'pause_fee_based' && values.agencyId !== undefined) {
                if (!agencies) return;
                const agency = agencies.find((agency) => agency.id === values.agencyId);
                updateBreakFee(agency, setfieldvalue);
            }

            if (newDeploymentType === 'nurse' || newDeploymentType === 'cancellation_period') {

                // check if location is franchise expert
                if (!isFranchiseExpert)
                    return;
                if (nurseDeploymentId === undefined || nurseDeploymentId === null) {
                    updateSystemFee(setfieldvalue);
                }
            }else {
                setfieldvalue('systemFee', 0.0);
            }

            updateBillingDates(values, setfieldvalue);

            setLastDeploymentType(newDeploymentType);
        }
    };

    const handleOnChange = (newvalues, initvalues, setfieldvalue) => {
        //console.log('newvalues=>', newvalues.type, 'initvalues=>', initialValues.type);
        handleOnChangeDeploymentType(newvalues.type, setfieldvalue, newvalues);

        if (newvalues.customerContractId !== null && newvalues.customerContractId !== '') {
            const customerContract = contracts.find(
                (contract) => contract.id === newvalues.customerContractId
            );
            updateContractInformation(customerContract, setfieldvalue);
        }

        if (
            newvalues.agencyId !== undefined &&
            newvalues.agencyId !== '' &&
            newvalues.agencyId !== null
        ) {
            if (!agencies) return;
            const agency = agencies.find((agency) => agency.id === newvalues.agencyId);
            const defaultCost = agency.defaultCostFeeBasedBrake;
            if (defaultCost === undefined || defaultCost === null || defaultCost <= 0.0)
                handleDisableBreakFeeBased();
            else handleEnableBreakFeeBased();
        } else {
            handleDisableBreakFeeBased();
        }
    };

    // some settings can only be changed if it's a new deployment or a copy
    const canChangeDeploymentType = (nurseDeployment === undefined || nurseDeployment === null);

    return (
        <Form
            initialValues={initialValues}
            onSubmit={handleSubmit}
            subject={nurseDeployment || NURSE_DEPLOYMENT_RESOURCE}
            validationSchema={nurseDepSchema}
            onChange={handleOnChange}
        >
            <Grid container spacing={2}>
                {is_fixated && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Diese Daten sind gesperrt{latestFixation && latestFixation.fixationMonth ? ` (Abrechnung ${new Date(latestFixation.fixationMonth).toLocaleString('de-DE', { month: 'long', year: 'numeric' })})` : ''}, für Änderungen bitte an die BuHa wenden!
                        </Alert>
                    </Grid>
                )}
                {is_fixation_overridden && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Diese Daten sind gesperrt{latestFixation && latestFixation.fixationMonth ? ` (fixiert bis ${new Date(latestFixation.fixationMonth).toLocaleString('de-DE', { month: 'long', year: 'numeric' })})` : ''}, aber Sie können sie ändern, da Sie Root oder BuHa sind!
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={5}>
                    <ToggleButtons name="type" options={optionsDeploymentTypes} exclusive disabled={!canChangeDeploymentType} />
                </Grid>
                {careNeeds.length > 1 && (
                    <Grid item xs={3}>
                        <CareNeedSelect
                            label="BK-Bedarf"
                            name="careNeedId"
                            customerId={customerId}
                            disabled={is_fixated}
                        />
                    </Grid>
                )}
                <Grid item xs={6}>
                    <ContractSelect
                        label="Vertrag"
                        name="customerContractId"
                        customerId={customerId}
                        disabled={is_fixated}
                    />
                    <AgencyByContractSelect 
                        name="agencyId" 
                        contractName="customerContractId"
                        disabled={is_fixated}
                    />
                    <Box component="span" display="none">
                        <DateInput 
                            name="customerContractEndAt" 
                             disabled={is_fixated}
                        />
                        <TextInput
                            name="fixedAgencyPrice"
                            label="Pause Fest-Preis Agentur"
                            type="number"
                            money
                            fullWidth
                            disabled={is_fixated}
                        />
                    </Box>
                </Grid>

                <ConditionalField
                    conditionFieldName="type"
                    condition={(value) => value === 'nurse'}
                >
                    <Grid item xs={6}>
                        <NurseAutocomplete 
                            name="nurseId" 
                            label="Betreuungskraft" 
                            fullWidth 
                            disabled={is_fixated}
                        />
                    </Grid>
                </ConditionalField>

                <ConditionalField
                    conditionFieldName="type"
                    condition={(value) => value === 'pause' || value === 'pause_fee_based'}
                >
                    <Grid item xs={6}>
                        <PauseReasonSelect 
                            name="pauseReason" 
                            label="Pausengrund" 
                            fullWidth 
                            disabled={is_fixated}
                        />
                    </Grid>
                </ConditionalField>

                <Grid item xs={12}>
                    <Box mt={5}>
                        <TitleBar>Einsatz</TitleBar>
                    </Box>
                </Grid>
                <Grid item xs={3}>
                    <DateInput
                        name="careFrom"
                        label={labelCareFrom}
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DateInput
                        name="careTill"
                        label={labelCareTill}
                        fullWidth
                        disabled={is_fixated_care_till}
                    />
                </Grid>
                <Grid item xs={6} />
                <CheckInOutDisable
                    nameArr="withoutArrTransfer"
                    labelArr="Anreise nicht erforderlich"
                    nameDep="withoutDepTransfer"
                    labelDep="Abreise nicht erforderlich"
                    arrivalAt={deploymentArrivalAt}
                    departureAt={deploymentDepartureAt}
                    xs={3}
                    disabled={is_fixated}
                />

                <Grid item xs={12}>
                    <Box mt={5}>
                        <TitleBar>Kosten</TitleBar>
                    </Box>
                </Grid>

                <Grid item xs={3}>
                    <TextInput
                        name="priceAgency"
                        label="Preis Agentur"
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextInput
                        name="surchargeAgency"
                        label="Zuschlag Agentur"
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextInput
                        name="provisionPZH"
                        label="Provision PZH"
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextInput
                        name="surchargePZH"
                        label="Zuschlag PZH"
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <ToggleButtons
                        name="priceAgencyDaily"
                        options={[
                            { value: true, label: 'pro Tag' },
                            { value: false, label: 'pro Monat' },
                        ]}
                        exclusive
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Typography>Endpreis Kunde</Typography>
                    <NursePriceFieldView />
                </Grid>
                <Grid item xs={3}>
                    <TextInput
                        name="provisionLocation"
                        label={
                            customer.multiplierId
                                ? 'davon Provi. Pflegedienst-Partner'
                                : 'davon Provi. Standort'
                        }
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextInput
                        name="systemFee"
                        label="Systemgebühr"
                        type="number"
                        money
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid item xs={3}>
                    <DateInput name="billingFrom" 
                               label="Berechnung vom" 
                               fullWidth 
                               disabled={is_fixated} 
                    />
                    <Checkbox name="billingFromHalf" label="Anreise halber Tag" disabled={is_fixated} />
                </Grid>
                <Grid item xs={3}>
                    <DateInput 
                        name="billingTill" 
                        label="Berechnung bis" 
                        fullWidth 
                        disabled={is_fixated_care_till} 
                    />
                    <Checkbox name="billingTillHalf" label="Abreise halber Tag" disabled={is_fixated_care_till} />
                </Grid>

                <Grid item xs={12}>
                    <Box mt={5}>
                        <TitleBar>Für folgenden Zeitraum wurden A1-Bescheinigungen nachgewiesen</TitleBar>
                    </Box>
                </Grid>
                <Grid item xs={3}>
                    <DateInput name="a1From" label="A1 vom" fullWidth disabled={is_fixated} />
                </Grid>
                <Grid item xs={3}>
                    <DateInput name="a1Till" label="A1 bis" fullWidth disabled={is_fixated} />
                </Grid>
                <Grid item xs={6}>
                    <TextInput
                        name="a1Notes"
                        label="A1 Bemerkungen"
                        rows={1}
                        rowsMax={6}
                        multiline
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
                {nurseDeploymentId && (
                    <Grid item xs={6}>
                        <A1DocumentList
                            nurseDeploymentId={nurseDeploymentId}
                            onChange={(list) => setDeleteA1DocList(list)}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Box mt={5} mb={1}>
                        <TitleBar>Notizen</TitleBar>
                    </Box>
                    <TextInput
                        name="notes"
                        label="Notizen"
                        rows={4}
                        rowsMax={10}
                        multiline
                        fullWidth
                        disabled={is_fixated}
                    />
                </Grid>
            </Grid>
            {is_fixated === false && (
                <Grid item xs={12}>
                    <Box mt={5}>
                        <DeleteButton
                        buttonLabel="BK-Einsatz löschen!"
                        confirmLabel='Diesen BK-Einsatz löschen?'
                        onClick={handleDelete} 
                    />
                    </Box>
                </Grid>
            )}
            <SubmitButton 
                container={submitRef} 
                disabled={is_fixated_submit}
            />

        </Form>
    );
};

NurseDeploymentForm.propTypes = {
    customerId: IdPropType,
    careNeedId: IdPropType,
    nurseDeploymentId: IdPropType,
    copy: PropTypes.bool,
    onDone: PropTypes.func.isRequired,
    submitRef: RefPropType,
};

NurseDeploymentForm.defaultProps = {
    customerId: null,
    careNeedId: null,
    nurseDeploymentId: null,
    copy: false,
    submitRef: null,
};

export default NurseDeploymentForm;
